import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { COLOURS } from '@tq/ui-components/dist/pp2/theme';
import { Box, Button, Container, DataTable, Flex, Grid, TableState } from '@tq/ui-components';

import { DataTablePagination, PageHeading } from 'components';
import {
  reportDataState,
  reportOrderByColumnState,
  reportOrderByDescendingState,
  reportPageNumberState,
  reportPageSizeState,
  reportPaginationState,
  reportSortByState,
  reportState,
} from 'store';
import { ReportData, ReportFilter } from 'types';
import { ReportDates, ReportFilters } from '..';
import { useReports } from '../../hooks';
import { UseMutateFunction } from '@tanstack/react-query';

type BaseReportProps = {
  filters: ReportFilter[];
  exportCsv: UseMutateFunction<unknown, unknown, unknown>;
};

export const BaseReport: React.FC<BaseReportProps> = ({ filters, exportCsv }) => {
  const { t } = useTranslation('report');
  const { name } = useParams();
  const reports = useReports();
  const tableRef = useRef(null);

  const [report, setReport] = useRecoilState(reportState);
  const reportData = useRecoilValue(reportDataState);
  const reportPagination = useRecoilValue(reportPaginationState);
  const reportSortBy = useRecoilValue(reportSortByState);
  const setReportOrderByColumn = useSetRecoilState(reportOrderByColumnState);
  const setReportOrderByDescending = useSetRecoilState(reportOrderByDescendingState);

  useEffect(() => {
    if (!report && name) {
      const currentReport = reports.find((x) => x.name === name);
      if (currentReport) {
        setReport(currentReport);
      }
    }
  }, [reports, report, setReport, name]);

  const handleStateChange = (state: TableState<ReportData>) => {
    const newOrderByColumn = state.sortBy?.[0]?.id;
    const newOrderByDescending = state.sortBy?.[0]?.desc ?? false;

    if (newOrderByColumn) {
      setReportOrderByColumn(newOrderByColumn);
      setReportOrderByDescending(newOrderByDescending);
    }
  };

  return (
    <Container px={{ base: 0, lg: 4, xl: 0 }} mb={6}>
      {report && (
        <Grid gridGap={4}>
          <Box>
            <Grid gridGap={4}>
              <Grid gridGap={2}>
                <Flex justifyContent='space-between'>
                  <PageHeading heading={report.header} />
                  <Button
                    size='sm'
                    w='180px'
                    variant='secondary'
                    onClick={() => exportCsv({})}
                  >
                    {t('export', 'Export CSV')}
                  </Button>
                </Flex>
                <p>{report.description}</p>
              </Grid>
              {report.hasDateFilter && (
                <Flex
                  justifyContent='space-between'
                  border={`1px solid ${COLOURS.gray[300]}`}
                  borderRadius={5}
                  p={4}
                  mt={2}
                >
                  <ReportDates />
                  <Button type='submit' form='report-dates-form'>
                    {t('update_report', 'Update report')}
                  </Button>
                </Flex>
              )}
              <ReportFilters filters={filters} />
            </Grid>
          </Box>
          <Box ref={tableRef} overflow='hidden'>
            <DataTable
              whiteSpace='normal'
              enableOverflowScrollingContainer
              data={reportData ?? []}
              columns={report.columns}
              onStateChange={handleStateChange}
              size='sm'
              sortBy={reportSortBy}
              disableSortRemove
              disablePagination
              manualSortBy
              sx={{
                '.tqc-datatable__th-content': {
                  minWidth: '130px',
                },
              }}
            />
          </Box>
          <Box>
            <DataTablePagination
              paginationInfo={reportPagination}
              pageNumberState={reportPageNumberState}
              pageSizeState={reportPageSizeState}
              tableRef={tableRef}
              tableScrollHeaderOffset={167}
            />
          </Box>
        </Grid>
      )}
    </Container>
  );
};
